// `count` / `sum` / `avg` are the original three. The rest (v2.2
// findings.md #46) exist because the recommended monitoring widgets can't
// be expressed correctly with only those:
// - `min` / `max` / `last` — general building blocks; `last` is "the value
//   on the latest row" (by `orderField`), e.g. current weight.
// - `sumOfLast` — for level quantities like fish stock or biomass, which
//   must not be summed across days: take each entity's (`entityField`,
//   e.g. lot) latest value in the cell, then sum across entities. A plain
//   weekly `sum` of daily stock rows is ~7x inflated.
// - `ratio` — Σ valueField / Σ denominatorField (× `ratioMultiplier`), e.g.
//   mortality % = Σ deaths / Σ stock × 100, FCR = Σ feed / Σ gain. An `avg`
//   of per-row ratios weights a 500-fish lot the same as a 9000-fish one.
// - `weightedAvg` — Σ (value × weightField) / Σ weightField, e.g. average
//   weight weighted by stock.
// Every aggregation's cell state is mergeable (see `addCell`), so row,
// column and grand totals stay correct — they're merged cells, not a
// re-aggregation of already-aggregated numbers.
export type PivotAggregation =
  | 'count'
  | 'sum'
  | 'avg'
  | 'min'
  | 'max'
  | 'last'
  | 'sumOfLast'
  | 'ratio'
  | 'weightedAvg'

export const PIVOT_AGGREGATIONS: readonly PivotAggregation[] = [
  'count',
  'sum',
  'avg',
  'min',
  'max',
  'last',
  'sumOfLast',
  'ratio',
  'weightedAvg'
]

export const isPivotAggregation = (value: unknown): value is PivotAggregation => (
  typeof value === 'string' && (PIVOT_AGGREGATIONS as readonly string[]).includes(value)
)

export type PivotConfig = {
  rowField: string
  columnField?: string
  valueField?: string
  aggregation: PivotAggregation
  maxRowKeys?: number
  maxColumnKeys?: number
  // Which row counts as "latest" for `last` / `sumOfLast`. Absent → input
  // order (later rows win).
  orderField?: string
  // Required for `sumOfLast`: the entity whose latest values get summed.
  entityField?: string
  // Required for `ratio`.
  denominatorField?: string
  // Required for `weightedAvg`.
  weightField?: string
  // `ratio` only; e.g. 100 for a percentage. Defaults to 1.
  ratioMultiplier?: number
  // When row keys exceed `maxRowKeys`, keep the last N (in sorted order)
  // instead of the first N. For date-bucketed rows, whose labels sort
  // chronologically, that means keeping the most recent buckets.
  keepLatestRowKeys?: boolean
}

type OrderedValue = {
  order: number | string | null
  seq: number
  value: number
}

export type PivotCell = {
  count: number
  // `sum`/`avg`: Σ value. `ratio`: Σ numerator (× multiplier).
  // `weightedAvg`: Σ value × weight.
  sum: number
  // `ratio`: Σ denominator. `weightedAvg`: Σ weight.
  denominator: number
  min: number | null
  max: number | null
  last: OrderedValue | null
  lastByEntity: Map<string, OrderedValue> | null
}

export type PivotResult = {
  rowKeys: string[]
  columnKeys: string[]
  cells: PivotCell[][]
  rowTotals: PivotCell[]
  columnTotals: PivotCell[]
  grandTotal: PivotCell
  truncated: {
    rows: boolean
    columns: boolean
  }
}

const toLabel = (value: unknown): string => {
  if (value === null || value === undefined) return '(blank)'
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : '(blank)'
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return '(value)'
}

const parseNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return null
    const normalized = trimmed.replace(',', '.')
    const parsed = Number(normalized)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

// Numbers compare numerically, date-like strings by timestamp, anything
// else lexically. Blank → null, which sorts before any real value.
const toOrderKey = (value: unknown): number | string | null => {
  if (value === null || value === undefined) return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null
  const text = value.trim()
  if (!text) return null
  const parsed = Date.parse(text)
  return Number.isNaN(parsed) ? text : parsed
}

const compareOrderKeys = (a: number | string | null, b: number | string | null): number => {
  if (a === b) return 0
  if (a === null) return -1
  if (b === null) return 1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b))
}

const laterOf = (a: OrderedValue | null, b: OrderedValue | null): OrderedValue | null => {
  if (!a) return b
  if (!b) return a
  const byOrder = compareOrderKeys(a.order, b.order)
  if (byOrder !== 0) return byOrder > 0 ? a : b
  return a.seq >= b.seq ? a : b
}

const createCell = (): PivotCell => ({
  count: 0,
  sum: 0,
  denominator: 0,
  min: null,
  max: null,
  last: null,
  lastByEntity: null
})

const mergeEntityMaps = (
  a: Map<string, OrderedValue> | null,
  b: Map<string, OrderedValue> | null
): Map<string, OrderedValue> | null => {
  if (!a) return b ? new Map(b) : null
  if (!b) return new Map(a)
  const next = new Map(a)
  b.forEach((value, entity) => {
    next.set(entity, laterOf(next.get(entity) ?? null, value) as OrderedValue)
  })
  return next
}

const addCell = (target: PivotCell, source: PivotCell): PivotCell => ({
  count: target.count + source.count,
  sum: target.sum + source.sum,
  denominator: target.denominator + source.denominator,
  min: target.min === null ? source.min : (source.min === null ? target.min : Math.min(target.min, source.min)),
  max: target.max === null ? source.max : (source.max === null ? target.max : Math.max(target.max, source.max)),
  last: laterOf(target.last, source.last),
  lastByEntity: mergeEntityMaps(target.lastByEntity, source.lastByEntity)
})

// Every aggregation except `count` / `sum` returns NaN for "no data" — a
// missing bucket is not a real zero (a zero stock, mortality or SGR is a
// meaningful value), and callers treat non-finite as "-" / skip it on the
// chart. `avg` used to return 0 here, which plotted false 0.00 points on
// e.g. the weekly SGR widget for weeks with no SGR rows (findings.md #47).
// `count` / `sum` of nothing genuinely is 0.
export const formatPivotValue = (cell: PivotCell, aggregation: PivotAggregation): number => {
  if (aggregation === 'count') return cell.count
  if (aggregation === 'sum') return cell.sum
  if (aggregation === 'avg') return cell.count > 0 ? cell.sum / cell.count : Number.NaN
  if (aggregation === 'min') return cell.min ?? Number.NaN
  if (aggregation === 'max') return cell.max ?? Number.NaN
  if (aggregation === 'last') return cell.last ? cell.last.value : Number.NaN
  if (aggregation === 'sumOfLast') {
    if (!cell.lastByEntity || cell.lastByEntity.size === 0) return Number.NaN
    let total = 0
    cell.lastByEntity.forEach((entry) => { total += entry.value })
    return total
  }
  if (aggregation === 'ratio' || aggregation === 'weightedAvg') {
    return cell.denominator !== 0 ? cell.sum / cell.denominator : Number.NaN
  }
  return 0
}

const requireField = (value: string, aggregation: PivotAggregation, name: string): void => {
  if (!value) throw new Error(`Pivot ${name} is required for ${aggregation}`)
}

export const buildPivotTable = (rows: Array<Record<string, unknown>>, config: PivotConfig): PivotResult => {
  const maxRowKeys = typeof config.maxRowKeys === 'number' && Number.isFinite(config.maxRowKeys)
    ? Math.max(1, Math.floor(config.maxRowKeys))
    : 50
  const maxColumnKeys = typeof config.maxColumnKeys === 'number' && Number.isFinite(config.maxColumnKeys)
    ? Math.max(1, Math.floor(config.maxColumnKeys))
    : 30

  const rowField = config.rowField.trim()
  const columnField = config.columnField?.trim() || ''
  const valueField = config.valueField?.trim() || ''
  const orderField = config.orderField?.trim() || ''
  const entityField = config.entityField?.trim() || ''
  const denominatorField = config.denominatorField?.trim() || ''
  const weightField = config.weightField?.trim() || ''
  const aggregation = config.aggregation
  const ratioMultiplier = typeof config.ratioMultiplier === 'number' && Number.isFinite(config.ratioMultiplier)
    ? config.ratioMultiplier
    : 1

  if (!rowField) {
    throw new Error('Pivot row field is required')
  }

  const needsValue = aggregation !== 'count'
  if (needsValue && !valueField) {
    throw new Error(`Pivot value field is required for ${aggregation}`)
  }
  if (aggregation === 'sumOfLast') requireField(entityField, aggregation, 'entity field')
  if (aggregation === 'ratio') requireField(denominatorField, aggregation, 'denominator field')
  if (aggregation === 'weightedAvg') requireField(weightField, aggregation, 'weight field')

  const rowKeySet = new Set<string>()
  const columnKeySet = new Set<string>()

  rows.forEach((row) => {
    rowKeySet.add(toLabel(row[rowField]))
    if (columnField) columnKeySet.add(toLabel(row[columnField]))
  })

  const rowKeysAll = Array.from(rowKeySet).sort((a, b) => a.localeCompare(b))
  const columnKeysAll = Array.from(columnKeySet).sort((a, b) => a.localeCompare(b))

  const truncatedRows = rowKeysAll.length > maxRowKeys
  const truncatedColumns = columnKeysAll.length > maxColumnKeys

  let rowKeys = rowKeysAll
  if (truncatedRows) {
    rowKeys = config.keepLatestRowKeys
      ? rowKeysAll.slice(rowKeysAll.length - maxRowKeys)
      : rowKeysAll.slice(0, maxRowKeys)
  }
  let columnKeys: string[] = []
  if (columnField) {
    columnKeys = truncatedColumns ? columnKeysAll.slice(0, maxColumnKeys) : columnKeysAll
  }

  const rowIndex = new Map(rowKeys.map((key, index) => [key, index] as const))
  const columnIndex = new Map(columnKeys.map((key, index) => [key, index] as const))

  const cells: PivotCell[][] = rowKeys.map(() => (
    columnKeys.length > 0
      ? columnKeys.map(() => createCell())
      : [createCell()]
  ))

  rows.forEach((row, seq) => {
    const rKey = toLabel(row[rowField])
    const r = rowIndex.get(rKey)
    if (r === undefined) return

    const cKey = columnField ? toLabel(row[columnField]) : '__single__'
    const c = columnField ? columnIndex.get(cKey) : 0
    if (c === undefined) return

    const cell = cells[r][c]

    if (!needsValue) {
      cell.count += 1
      return
    }

    const amount = parseNumber(row[valueField])
    if (amount === null) return

    // `ratio` / `weightedAvg` are pairwise-complete: a row only counts
    // when both its value and its denominator/weight are present, so a
    // missing stock reading can't silently shrink a denominator.
    if (aggregation === 'ratio' || aggregation === 'weightedAvg') {
      const second = parseNumber(row[aggregation === 'ratio' ? denominatorField : weightField])
      if (second === null) return
      cell.count += 1
      if (aggregation === 'ratio') {
        cell.sum += amount * ratioMultiplier
        cell.denominator += second
      } else {
        cell.sum += amount * second
        cell.denominator += second
      }
      return
    }

    cell.count += 1
    cell.sum += amount
    cell.min = cell.min === null ? amount : Math.min(cell.min, amount)
    cell.max = cell.max === null ? amount : Math.max(cell.max, amount)

    if (aggregation === 'last' || aggregation === 'sumOfLast') {
      const entry: OrderedValue = {
        order: orderField ? toOrderKey(row[orderField]) : null,
        seq,
        value: amount
      }
      cell.last = laterOf(cell.last, entry)
      if (aggregation === 'sumOfLast') {
        const entity = toLabel(row[entityField])
        if (!cell.lastByEntity) cell.lastByEntity = new Map()
        cell.lastByEntity.set(entity, laterOf(cell.lastByEntity.get(entity) ?? null, entry) as OrderedValue)
      }
    }
  })

  const rowTotals = rowKeys.map((_, r) => {
    return cells[r].reduce((acc, cell) => addCell(acc, cell), createCell())
  })

  const columnTotals = (columnKeys.length > 0 ? columnKeys : ['__single__']).map((_, c) => {
    return cells.reduce((acc, row) => addCell(acc, row[c] ?? createCell()), createCell())
  })

  const grandTotal = columnTotals.reduce((acc, cell) => addCell(acc, cell), createCell())

  return {
    rowKeys,
    columnKeys,
    cells,
    rowTotals,
    columnTotals,
    grandTotal,
    truncated: {
      rows: truncatedRows,
      columns: truncatedColumns
    }
  }
}
